import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { InstallAppointment } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  getInstallAppointmentConfirmationPollInterval,
  INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_INTERVAL_MS,
  INSTALL_APPOINTMENT_CONFIRMATION_POLL_DURATION_MS,
  isInstallAppointmentCustomerConfirmed,
} from "../constants/capInstallScheduling";
import { useAuthToken } from "hooks/useAuthToken";
import { getInstallAppointment } from "services/installScheduling";

const useInstallAppointmentConfirmation = (
  prospectId: string | undefined,
  appointmentId: string | undefined
) => {
  const authKey = useAuthToken("HYBRID");
  const pollStartedAtRef = useRef<number | null>(null);
  const [pollGeneration, setPollGeneration] = useState(0);
  const [isPollingExpired, setIsPollingExpired] = useState(false);
  const shouldFetch = Boolean(prospectId && appointmentId && authKey?.token);

  const { data, isLoading, isValidating } = useSWR(
    shouldFetch
      ? [
          "Get Install Appointment",
          prospectId,
          appointmentId,
          authKey!.token,
          pollGeneration,
        ]
      : null,
    () => getInstallAppointment(authKey!.token, prospectId!, appointmentId!),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: (latestData?: InstallAppointment) => {
        if (isInstallAppointmentCustomerConfirmed(latestData)) {
          return 0;
        }

        const pollStartedAt = pollStartedAtRef.current;
        if (pollStartedAt == null) {
          return INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_INTERVAL_MS;
        }

        return getInstallAppointmentConfirmationPollInterval(
          Date.now() - pollStartedAt
        );
      },
    }
  );

  const isCustomerConfirmed = isInstallAppointmentCustomerConfirmed(
    data as InstallAppointment | undefined
  );

  useEffect(() => {
    if (!shouldFetch || isCustomerConfirmed) {
      pollStartedAtRef.current = null;
      setIsPollingExpired(false);
      return;
    }

    pollStartedAtRef.current = Date.now();
    setIsPollingExpired(false);

    const timeoutId = window.setTimeout(() => {
      setIsPollingExpired(true);
    }, INSTALL_APPOINTMENT_CONFIRMATION_POLL_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    shouldFetch,
    prospectId,
    appointmentId,
    pollGeneration,
    isCustomerConfirmed,
  ]);

  const refreshConfirmation = useCallback(() => {
    pollStartedAtRef.current = Date.now();
    setIsPollingExpired(false);
    setPollGeneration((generation) => generation + 1);
  }, []);

  return {
    isCustomerConfirmed,
    isLoading,
    isPollingExpired,
    isRefreshing: isValidating,
    refreshConfirmation,
  } as const;
};

export { useInstallAppointmentConfirmation };
